import { useState, useMemo } from "react";
import styles from "../../styles/evento-detalle.module.css";



export function TabActividades({ evento, catalogos }) {
    const [openSet, setOpenSet] = useState(new Set());
    const personasSafe = Array.isArray(catalogos?.personas) ? catalogos.personas : [];

    const toggle = (idx) => {
        setOpenSet((prev) => {
            const next = new Set(prev);
            next.has(idx) ? next.delete(idx) : next.add(idx);
            return next;
        });
    };
    const isOpen = (idx) => openSet.has(idx);

    const data = useMemo(() => {
        const acts = Array.isArray(evento?.actividades) ? evento.actividades : [];
        const parts = Array.isArray(evento?.participaciones) ? evento.participaciones : [];

        // Normaliza persona label
        const getPersonaLabel = (p) => {
            const per = p?.persona || personasSafe.find(x => x.id === p?.personaId);
            if (!per) return String(p?.personaId ?? "—");
            return `${per.nombre} ${per.apellidos}`;
        };

        // Construimos un índice actividadId -> Set(personaId)
        const actToPersonaIds = new Map();

        // Caso A: participaciones traen actividades como objetos
        parts.forEach((p) => {
            const personaId = p?.persona?.id ?? p?.personaId ?? null;
            if (!personaId) return;

            const actObjs = Array.isArray(p?.actividades) ? p.actividades : [];
            actObjs.forEach((a) => {
                const actId = a?.id;
                if (actId == null) return;
                const set = actToPersonaIds.get(Number(actId)) || new Set();
                set.add(Number(personaId));
                actToPersonaIds.set(Number(actId), set);
            });
        });

        // Caso B: actividades traen participanteIds directamente (como en tu form)
        // Si existe, lo priorizamos (porque es explícito por actividad).
        const actToPersonaIdsFromActivity = new Map();
        acts.forEach((a) => {
            const ids = Array.isArray(a?.participanteIds) ? a.participanteIds : [];
            if (!ids.length) return;
            actToPersonaIdsFromActivity.set(
                Number(a.id),
                new Set(ids.map((x) => Number(x)))
            );
        });

        // Resultado final por actividad
        const out = acts.map((a, idx) => {
            const actId = a?.id != null ? Number(a.id) : null;

            const personaIds =
                actId != null && actToPersonaIdsFromActivity.has(actId)
                    ? actToPersonaIdsFromActivity.get(actId)
                    : actId != null
                        ? (actToPersonaIds.get(actId) || new Set())
                        : new Set();

            // Mapa personaId -> label
            const personasList = Array.from(personaIds)
                .map((pid) => {
                    const per = personasSafe.find((x) => Number(x.id) === Number(pid));
                    return {
                        id: pid,
                        label: per ? `${per.nombre} ${per.apellidos}` : String(pid),
                    };
                })
                .sort((x, y) => String(x.label).localeCompare(String(y.label), "es"));

            return {
                key: actId ?? `idx-${idx}`,
                id: actId,
                nombre: a?.nombre || `Actividad ${idx + 1}`,
                tipo: a?.tipo || "",
                fecha: a?.fecha || "",
                personas: personasList,
            };
        });

        // Orden por fecha y nombre
        out.sort((x, y) => {
            const a = String(x.fecha || "");
            const b = String(y.fecha || "");
            const c = a.localeCompare(b);
            if (c !== 0) return c;
            return String(x.nombre || "").localeCompare(String(y.nombre || ""), "es");
        });

        return out;
    }, [evento, personasSafe]);

    if (!data.length) {
        return <div className={styles.hint}>Este evento no tiene actividades.</div>;
    }

    return (
        <div className={styles.stack}>
            {data.map((a, idx) => {
                const open = isOpen(idx);
                const resumen = [
                    a.fecha ? a.fecha.split("T")[0] : null,
                    a.tipo || null,
                    `${a.personas.length} participante(s)`,
                ].filter(Boolean).join(" • ");

                return (
                    <div
                        key={a.key}
                        className={`${styles["item-card"]} ${styles.accordion} ${open ? styles.open : ""}`}
                    >
                        <button
                            type="button"
                            className={styles["accordion-header"]}
                            onClick={() => toggle(idx)}
                            aria-expanded={open}
                            aria-controls={`act-body-${idx}`}
                        >
                            <div className={styles["accordion-title"]}>
                                <strong>{a.nombre}</strong>
                                <span className={styles.muted}>— {resumen}</span>
                            </div>
                            <span className={styles.chevron} aria-hidden>▾</span>
                        </button>

                        <div id={`act-body-${idx}`} className={styles["accordion-body"]}>
                            {a.personas.length === 0 ? (
                                <div className={styles.hint}>No hay participantes asignados a esta actividad.</div>
                            ) : (
                                <div className={styles["table-wrap"]}>
                                    <table className={styles["participaciones-table"]}>
                                        <thead>
                                            <tr>
                                                <th>Persona</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {a.personas.map((p) => (
                                                <tr key={p.id}>
                                                    <td>{p.label}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}